/**
 * @fileoverview GenericForm Types and Documentation
 *
 * GenericForm is a powerful wrapper around React Hook Form that provides:
 *
 * ## Key Features:
 * - 🔄 Automatic API integration with TanStack Query
 * - ✅ Built-in Yup validation with field-level error handling
 * - 💾 Auto-save functionality with debouncing
 * - 🍞 Toast notifications for success/error states
 * - 🔒 Automatic CSRF token handling
 * - 🎯 TypeScript-first with full type safety
 *
 * ## Basic Usage:
 * ```tsx
 * interface UserForm {
 *   name: string
 *   email: string
 * }
 *
 * const userSchema = Yup.object().shape({
 *   name: Yup.string().required("Name is required"),
 *   email: Yup.string().email("Invalid email").required("Email is required")
 * })
 *
 * <GenericForm<UserForm, User, ApiError, UserForm>
 *   formConfig={{ defaultValues: { name: "", email: "" } }}
 *   formValidationSchema={userSchema}
 *   mutationConfig={{
 *     endpoint: "/api/users",
 *     method: "POST",
 *     genericErrorMessage: "Failed to create user"
 *   }}
 *   onMutationSuccess={({ data }) => {
 *     console.log("User created:", data)
 *     // Handle success (redirect, show toast, etc.)
 *   }}
 * >
 *   {({ isPending, form, nonFieldError }) => (
 *     <>
 *       {nonFieldError && <ErrorMessage errors={nonFieldError} />}
 *       <TextInput name="name" placeholder="Full name" />
 *       <EmailInput name="email" placeholder="Email address" />
 *       <Button type="submit" isProcessing={isPending}>
 *         Create User
 *       </Button>
 *     </>
 *   )}
 * </GenericForm>
 * ```
 *
 * ## Auto-Save Usage:
 * ```tsx
 * <GenericForm
 *   isAutoSave={true}
 *   autoSaveDebounceTimeMs={1000}
 *   mutationConfig={{
 *     endpoint: "/api/user/profile",
 *     method: "PATCH",
 *     genericErrorMessage: "Failed to save changes"
 *   }}
 * >
 *   {({ form }) => (
 *     <TextInput name="bio" placeholder="Tell us about yourself" />
 *     // Form auto-saves 1 second after user stops typing
 *   )}
 * </GenericForm>
 * ```
 *
 * ## Error Handling:
 * - Field-level errors: Automatically displayed on form fields
 * - Non-field errors: Available via `nonFieldError` in children function
 * - Generic errors: Shown as toast notifications
 * - Custom error handling: Use `onMutationError` callback
 *
 * ## Best Practices:
 * 1. Always provide `genericErrorMessage` for better UX
 * 2. Use `payloadFormatter` when API expects different data structure
 * 3. Handle success/error states in callbacks for complex flows
 * 4. Use `formValues` prop to sync with external data sources
 * 5. Enable auto-save for forms that don't need explicit submission
 */
import { DefaultValues, FieldValues, UseFormProps, UseFormReturn } from "react-hook-form"
import * as Yup from "yup"

/**
 * Props passed to the children render function of GenericForm.
 * Provides access to form state, validation, and mutation status.
 */
type ChildrenProps<FormFields extends FieldValues> = {
    /** Indicates if a mutation is currently in progress */
    isPending: boolean
    /** Non-field specific errors (e.g., server errors, authentication errors) */
    nonFieldError: string[] | undefined
    /** React Hook Form instance with all form methods and state */
    form: UseFormReturn<FormFields>
}

/**
 * Base type for form field values - all fields must be strings.
 * Used internally for type safety and validation.
 */
type FormFieldsBase = {
    [key: string]: string
}

/**
 * Extended form fields type that includes support for non-field errors.
 * Django REST Framework uses `non_field_errors` and Django forms use `__all__`
 * for general form errors that don't belong to specific fields.
 */
type FormFieldsBaseWithNonFieldError = FormFieldsBase & { __all__?: string[]; non_field_errors?: string[] }

/**
 * Represents the base structure for mutation errors within forms.
 * This type can accommodate various error formats:
 *
 * 1. Object with keys corresponding to form fields and values as an array of error messages:
 *    {
 *      fieldName: ["Error message 1", "Error message 2"],
 *      anotherFieldName: ["Another error message"]
 *    }
 *
 * 2. A simple string for general error messages:
 *    "A general error occurred."
 *
 * 3. An Error object for system-level errors:
 *    new Error("A system error occurred.")
 *
 * Usage should be tailored to the context of the form and the nature of errors encountered.
 */
export type MutationErrorBase =
    | {
          [key in keyof FormFieldsBaseWithNonFieldError]: string[]
      }
    | string
    | Error

/**
 * Configuration object for React Hook Form setup.
 * Controls form behavior, validation timing, and default values.
 */
type FormConfig<FormFields> = {
    /** When form validation should occur. Default: "onTouched" */
    mode?: UseFormProps["mode"]
    /** When re-validation should occur after first validation. Default: "onChange" */
    reValidateMode?: UseFormProps["reValidateMode"]
    /** Initial values for form fields */
    defaultValues?: DefaultValues<FormFields>
}

/**
 * Parameters passed to the onMutationSuccess callback.
 * Contains both the server response data and current form values.
 */
export type MutationSuccessFunctionParams<FormFields, MutationReturn> = {
    /** Data returned from the successful mutation request */
    data: MutationReturn
    /** Current form field values at the time of success */
    formValues: FormFields
}

/**
 * Configuration for API mutation requests.
 * Defines endpoint, method, error handling, and payload transformation.
 */
type MutationConfig<FormFields, MutationPayload> = {
    /** API endpoint URL for the mutation request */
    endpoint: string
    /** HTTP method for the request */
    method: "POST" | "PATCH" | "DELETE"
    /** Fallback error message shown when no specific error is available */
    genericErrorMessage: string
    /** Success message shown when mutation completes successfully (optional) */
    genericSuccessMessage?: string
    /** Function to transform form data before sending to API (optional) */
    payloadFormatter?: (formValues: FormFields) => MutationPayload
    /** Disable mutation requests (useful for read-only forms) */
    disabled?: boolean
}

/**
 * Main props interface for the GenericForm component.
 *
 * GenericForm is a powerful wrapper around React Hook Form that provides:
 * - Automatic API integration with TanStack Query
 * - Built-in error handling and validation
 * - Auto-save functionality
 * - Toast notifications for success/error states
 * - CSRF token handling
 *
 * @template FormFields - TypeScript interface defining form field structure
 * @template MutationReturn - Expected return type from API mutation
 * @template MutationError - Expected error structure from API
 * @template MutationPayload - Data structure sent to API (may differ from FormFields)
 *
 * @example
 * ```tsx
 * interface UserFormFields {
 *   name: string
 *   email: string
 * }
 *
 * <GenericForm<UserFormFields, User, ApiError, UserFormFields>
 *   formConfig={{ defaultValues: { name: "", email: "" } }}
 *   formValidationSchema={userSchema}
 *   mutationConfig={{
 *     endpoint: "/api/users",
 *     method: "POST",
 *     genericErrorMessage: "Failed to create user"
 *   }}
 *   onMutationSuccess={({ data }) => console.log("User created:", data)}
 * >
 *   {({ isPending, form }) => (
 *     <EmailInput name="email" />
 *     <Button type="submit" isProcessing={isPending}>
 *       Create User
 *     </Button>
 *   )}
 * </GenericForm>
 * ```
 */
export interface GenericFormProps<
    FormFields extends FieldValues,
    MutationReturn,
    MutationError extends MutationErrorBase,
    MutationPayload,
> {
    /**
     * Render function that receives form state and returns JSX.
     * Provides access to form instance, loading state, and errors.
     *
     * @param isPending - True when mutation is in progress
     * @param nonFieldError - General errors not tied to specific fields
     * @param form - React Hook Form instance with all methods and state
     */
    children: ({ isPending, nonFieldError, form }: ChildrenProps<FormFields>) => React.ReactNode

    // ===== FORM CONFIGURATION =====

    /**
     * React Hook Form configuration object.
     * Controls form behavior, validation timing, and default values.
     *
     * @example
     * ```tsx
     * formConfig={{
     *   mode: "onBlur",
     *   reValidateMode: "onChange",
     *   defaultValues: { name: "", email: "" }
     * }}
     * ```
     */
    formConfig: FormConfig<FormFields>

    /**
     * Yup validation schema for form validation.
     * Can be a static schema or a lazy schema for dynamic validation.
     *
     * @example
     * ```tsx
     * formValidationSchema={Yup.object().shape({
     *   email: Yup.string().email().required(),
     *   name: Yup.string().min(2).required()
     * })}
     * ```
     */
    formValidationSchema?: Yup.ObjectSchema<FormFields> | ReturnType<typeof Yup.lazy<Yup.ObjectSchema<FormFields>>>

    /**
     * External form values to sync with the form.
     * When provided, form will reset to these values.
     * Useful for loading existing data or external state updates.
     *
     * @example
     * ```tsx
     * formValues={user ? { name: user.name, email: user.email } : undefined}
     * ```
     */
    formValues?: FormFields

    /**
     * Callback fired after form values are set from external source.
     * Useful for side effects after form reset.
     */
    onAfterFormValuesSet?: () => void

    /**
     * Triggers a complete form reset when changed to true.
     * Useful for clearing form after successful submission.
     */
    triggerFormReset?: boolean

    // ===== MUTATION CONFIGURATION =====

    /**
     * API mutation configuration.
     * Defines endpoint, method, error messages, and payload transformation.
     *
     * @example
     * ```tsx
     * mutationConfig={{
     *   endpoint: "/api/users",
     *   method: "POST",
     *   genericErrorMessage: "Failed to create user",
     *   genericSuccessMessage: "User created successfully",
     *   payloadFormatter: (data) => ({ user: data })
     * }}
     * ```
     */
    mutationConfig: MutationConfig<FormFields, MutationPayload>

    /**
     * Callback fired when mutation encounters an error.
     * Receives the error object with both message and detailed error info,
     * plus the form instance for custom error handling (e.g., mapping non_field_errors to specific fields).
     *
     * @param error - GenericError containing message and detailed error
     * @param form - React Hook Form instance for custom error handling
     */
    onMutationError?: (error: GenericError<MutationError>, form: UseFormReturn<FormFields>) => void

    /**
     * Callback fired when mutation succeeds.
     * Receives both API response data and current form values.
     *
     * @param params - Object containing API response and form values
     */
    onMutationSuccess?: (params: MutationSuccessFunctionParams<FormFields, MutationReturn>) => void

    /**
     * Triggers mutation when provided with a payload.
     * Useful for programmatic form submission or external triggers.
     */
    mutationTriggerPayload?: MutationPayload

    // ===== AUTO-SAVE CONFIGURATION =====

    /**
     * Enables auto-save functionality.
     * Form will automatically submit on field changes after debounce delay.
     * Disables manual form submission.
     */
    isAutoSave?: boolean

    /**
     * Debounce delay in milliseconds for auto-save.
     * Default: 500ms (DEFAULT_DEBOUNCE_TIME)
     */
    autoSaveDebounceTimeMs?: number

    // ===== RENDERING OPTIONS =====

    /**
     * Removes the HTML form tag wrapper.
     * Useful when form needs to be rendered inside another form element.
     */
    noFormTag?: boolean

    /**
     * Additional CSS classes for the form wrapper.
     */
    className?: string
}

/**
 * Custom error class for handling API mutation errors in GenericForm.
 *
 * Extends the native Error class to include both a user-friendly message
 * and the detailed error response from the API.
 *
 * @template MutationError - The expected error structure from the API
 *
 * @example
 * ```tsx
 * // API returns: { email: ["Email already exists"] }
 * const error = new GenericError("Failed to create user", { email: ["Email already exists"] })
 *
 * console.log(error.message) // "Failed to create user"
 * console.log(error.error)   // { email: ["Email already exists"] }
 * ```
 */
export class GenericError<MutationError> extends Error {
    /** Detailed error response from the API */
    public error: MutationError

    /**
     * Creates a new GenericError instance.
     *
     * @param message - User-friendly error message
     * @param mutationError - Detailed error response from API
     */
    constructor(message: string, mutationError: MutationError) {
        super(message)
        this.name = "GenericError"
        this.error = mutationError
    }
}
